import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

@Component({
  selector: 'rms-select',
  standalone: true,
  templateUrl: './select-field.component.html',
  styleUrls: [
    './select-field.component.scss',
    '../rms-styles.scss'
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFieldComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class SelectFieldComponent implements ControlValueAccessor {
  @Input() label = '';

  @Input()
  options: Array<Record<string, any>> = [];

  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() includeSearch = false;
  @Input() labelKey = 'label';
  @Input() valueKey = 'value';
  @Input() PDF = false;

  @Output()
  selectionChange = new EventEmitter<any>();

  value: any = null;
  searchTerm = '';

  private onChangeCallback: (value: any) => void = () => { };
  private onTouchedCallback: () => void = () => { };

  writeValue(value: any): void {
    // Match the placeholder option's null value so the browser keeps it selected.
    this.value = value == null || value === '' ? null : value;
  }

  registerOnChange(
    callback: (value: any) => void
  ): void {
    this.onChangeCallback = callback;
  }

  registerOnTouched(
    callback: () => void
  ): void {
    this.onTouchedCallback = callback;
  }

  setDisabledState(
    isDisabled: boolean
  ): void {
    this.disabled = isDisabled;
  }

  handleChange(value: any): void {
    this.value = value;

    this.onChangeCallback(value);
    this.onTouchedCallback();
    this.selectionChange.emit(value);
  }

  onTouched(): void {
    this.onTouchedCallback();
  }

  get filteredOptions(): Array<Record<string, any>> {
    if (!Array.isArray(this.options)) {
      return [];
    }

    const search = this.searchTerm
      .trim()
      .toLowerCase();

    if (!search) {
      return this.options;
    }

    return this.options.filter(option =>
      Object.values(option).some(value =>
        String(value ?? '')
          .toLowerCase()
          .includes(search)
      )
    );
  }

  checkSearchLength(): void {
    if (this.filteredOptions.length !== 1) {
      return;
    }

    const selectedValue =
      this.filteredOptions[0][this.valueKey];

    this.handleChange(selectedValue);
  }

  focusSelect(
    selectElement: HTMLSelectElement
  ): void {
    if (this.disabled) {
      return;
    }

    selectElement.focus();
    this.tryShowPicker(selectElement);
  }

  tryOpenPicker(
    selectElement: HTMLSelectElement
  ): void {
    if (this.disabled) {
      return;
    }

    this.tryShowPicker(selectElement);
  }

  onSelectKeydown(
    event: KeyboardEvent,
    selectElement: HTMLSelectElement
  ): void {
    if (this.disabled) {
      return;
    }

    const openKeys = [
      'Enter',
      ' ',
      'ArrowDown',
      'Down'
    ];

    if (!openKeys.includes(event.key)) {
      return;
    }

    event.preventDefault();
    this.deferShowPicker(selectElement);
  }

  private tryShowPicker(
    selectElement: HTMLSelectElement
  ): void {
    const enhancedSelect =
      selectElement as HTMLSelectElement & {
        showPicker?: () => void;
      };

    if (
      typeof enhancedSelect.showPicker ===
      'function'
    ) {
      enhancedSelect.showPicker();
    }
  }

  private deferShowPicker(
    selectElement: HTMLSelectElement
  ): void {
    const enhancedSelect =
      selectElement as HTMLSelectElement & {
        showPicker?: () => void;
      };

    if (
      typeof enhancedSelect.showPicker !==
      'function'
    ) {
      return;
    }

    setTimeout(() => {
      enhancedSelect.showPicker?.();
    });
  }
  openOnMouseDown(evt: MouseEvent, selectEl: HTMLSelectElement) {
    if (this.disabled) return;

    evt.preventDefault();
    selectEl.focus();
    this.deferShowPicker(selectEl);
  }
}
